import Head from 'next/head'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { getSeoDetails, setPageSeo } from '@/store/slices/headSlice'

const Header = ({ seo }) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const reduxSeo = useSelector(getSeoDetails)
  const [hydrated, setHydrated] = useState(false)

  // Seed Redux with the SSR-provided per-page SEO on first paint and on client
  // navigation. This effect runs before the page's own effects, so any page
  // dispatch (e.g. setSpeakersSeo from a subcategory selection) wins over it.
  useEffect(() => {
    if (seo) dispatch(setPageSeo(seo))
    setHydrated(true)
  }, [seo, dispatch])

  // Server + first client render use the SSR prop (so the HTML is correct for
  // crawlers and hydration matches). After hydration the head follows Redux, so
  // client-side updates take effect again.
  const data = hydrated ? reduxSeo || seo : seo || reduxSeo
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL.replace(/\/$/, '')
  // Home canonical is the root with slash (https://site.com/); all other routes
  // use their non-trailing-slash path, matching the sitemap <loc> entries.
  const canonicalUrl = (baseUrl + (router.asPath === '/' ? '/' : router.asPath)).split('?')[0]
  const defaultKeywords =
    'UTA Speakers, keynote speakers, celebrity speakers, professional speakers, conference speakers, corporate speakers, United Talent Agency'
  const keywords = data?.keywords || defaultKeywords

  return (
    <>
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        <link rel="icon" href="https://www.unitedtalent.com/favicon.ico" />
        {process.env.NEXT_PUBLIC_NODE_ENV === 'development' ||
        process.env.NEXT_PUBLIC_NODE_ENV === 'staging' ? (
          <meta name="robots" content="noindex" />
        ) : (
          ''
        )}
      </Head>
      <Head>
        <title>{data?.pageTitle}</title>
        <meta property="author" content="Speakers | United Talent Agency" key="author" />
        <meta property="description" content={data?.pageDescription} key="description" />
        <meta name="description" content={data?.pageDescription} key="name-description" />
        <meta name="keywords" content={keywords} key="keywords" />
        <meta property="og:title" content={data?.pageTitle} key="og-title" />
        <meta property="og:description" content={data?.pageDescription} key="og-description" />
        <meta property="og:image" content={data?.ogImage} key="og-image" />
        <meta property="og:url" content={canonicalUrl} key="og-url" />
        <meta property="og:type" content={data?.ogType || 'website'} key="og-type" />
      </Head>
    </>
  )
}

export default Header
